/**
 * Default empty objects for various placement entities
 * These match the structure used in GRLCPlacementEditor
 */

export const createEmptyAmount = () => ({
  shareCode: "",
  ccyIndicator: "",
  ccy: "",
  value: 0,
});

export const createEmptyPercentage = () => ({
  value: 0,
  unitCode: "",
});

export const createEmptyDateTime = () => ({
  value: "",
  timeZoneCode: "",
});

export const createEmptyParty = () => ({
  type: "",
  id: "",
});

export const createEmptyClass = () => ({
  id: crypto.randomUUID(),
  agencyCode: "",
  agencyCodeSetCode: "",
  code: "",
  description: "",
  allocationPercentage: createEmptyPercentage(),
  ruleCode: "",
});

export const createEmptyPeril = () => ({
  id: crypto.randomUUID(),
  agencyCode: "",
  agencyCodeSetCode: "",
  code: "",
  description: "",
  allocationPercentage: createEmptyPercentage(),
  ruleCode: "",
});

export const createEmptyLocation = () => ({
  id: crypto.randomUUID(),
  type: "",
  agencyCode: "",
  agencyCodeSetCode: "",
  code: "",
  countryCode: "",
  subentityCode: "",
  supraentityCode: "",
  description: "",
  allocationPercentage: createEmptyPercentage(),
  ruleCode: "",
});

export const createEmptyCoverage = () => ({
  id: crypto.randomUUID(),
  type: "",
  description: "",
  amount: createEmptyAmount(),
  percentage: createEmptyPercentage(),
  basis: [
    {
      code: "",
      operation: "",
      subject: "",
      description: "",
    },
  ],
  numberOfLines: 0,
  numberOfUnits: 0,
});

export const createEmptyDeductible = () => ({
  id: crypto.randomUUID(),
  type: "",
  description: "",
  amount: createEmptyAmount(),
  percentage: createEmptyPercentage(),
  basis: [
    {
      code: "",
      operation: "",
      subject: "",
      description: "",
    },
  ],
  numberOfLines: 0,
  numberOfUnits: 0,
});

export const createEmptyInstalment = () => ({
  id: crypto.randomUUID(),
  sequenceNbr: 0,
  amount: createEmptyAmount(),
  percentage: createEmptyPercentage(),
  rate: createEmptyPercentage(),
  minimumRate: createEmptyPercentage(),
  maximumRate: createEmptyPercentage(),
  incomeAmountBasis: "",
  dueDateTime: {
    timeZoneCode: "",
    qualifier: "",
    estimatedIndicator: "",
    value: "",
  },
  termsOfTradePeriodDuration: 0,
  termsOfTradePeriodBasis: "",
  payableByDateTime: {
    timeZoneCode: "",
    qualifier: "",
    estimatedIndicator: "",
    value: "",
  },
  description: "",
});

export const createEmptyPremium = () => ({
  id: crypto.randomUUID(),
  type: "",
  description: "",
  percentage: createEmptyPercentage(),
  amount: createEmptyAmount(),
  adjustableIndicator: "",
  rate: createEmptyPercentage(),
  minimumRate: createEmptyPercentage(),
  maximumRate: createEmptyPercentage(),
  incomeAmountBasis: "",
  termsOfTradePeriodDays: 0,
  numberOfInstalments: 0,
  instalments: [],
});

export const createEmptyTax = () => ({
  id: crypto.randomUUID(),
  description: "",
  applicationType: "",
  classCode: "",
  type: "",
  locations: [],
  rateBasis: "",
  payableByParty: createEmptyParty(),
  administratorParty: createEmptyParty(),
  baseAmount: createEmptyAmount(),
  rate: createEmptyPercentage(),
  unitCount: 0,
  amount: createEmptyAmount(),
});

export const createEmptyCommissionValue = () => ({
  basis: "",
  sequenceNbr: 0,
  percentage: createEmptyPercentage(),
  lossRatioPercentage: createEmptyPercentage(),
  cedingCommissionPercentage: createEmptyPercentage(),
  amount: createEmptyAmount(),
});

export const createEmptyCommission = () => ({
  id: crypto.randomUUID(),
  type: "",
  description: "",
  classCode: "",
  basis: "",
  interpolationBasis: "",
  lossRatioBasis: "",
  carryForwardCode: "",
  carryForwardIntervalPeriodType: "",
  carryForwardInterval: 0,
  calculationType: "",
  calculationFrequencyCode: "",
  firstCalculationDateTime: createEmptyDateTime(),
  calculationStartDateTime: createEmptyDateTime(),
  calculationEndDateTime: createEmptyDateTime(),
  managementExpensesPercentage: createEmptyPercentage(),
  payableToParty: createEmptyParty(),
  payableByParty: createEmptyParty(),
  commissionValues: [],
});

export const createEmptyOtherDeduction = () => ({
  id: crypto.randomUUID(),
  type: "",
  description: "",
  classCode: "",
  percentage: createEmptyPercentage(),
  basis: "",
  amount: createEmptyAmount(),
  payableToParty: createEmptyParty(),
  payableByParty: createEmptyParty(),
  serviceProvidedToParty: createEmptyParty(),
});

export const createEmptyBrokerage = () => ({
  id: crypto.randomUUID(),
  type: "",
  description: "",
  classCode: "",
  percentage: createEmptyPercentage(),
  basis: "",
  amount: createEmptyAmount(),
  baseAmount: createEmptyAmount(),
  payableToParty: createEmptyParty(),
  payableByParty: createEmptyParty(),
});

export const createEmptyReinstatement = () => ({
  id: crypto.randomUUID(),
  sequenceNbr: 0,
  numberOfReinstatements: 0,
  premium: createEmptyPercentage(),
  brokerage: createEmptyPercentage(),
  provisions: "",
});

export const createEmptyContractParty = () => ({
  id: crypto.randomUUID(),
  partyReference: createEmptyParty(),
  role: "",
  roleDescription: "",
  effectiveDateTime: createEmptyDateTime(),
  expirationDateTime: createEmptyDateTime(),
});

export const createEmptyMarket = () => ({
  id: crypto.randomUUID(),
  partyReference: createEmptyParty(),
  role: "",
  roleDescription: "",
  share: createEmptyPercentage(),
  writtenLinePercentage: createEmptyPercentage(),
  signedLinePercentage: createEmptyPercentage(),
  orderPercentage: createEmptyPercentage(),
  brokerPercentage: createEmptyPercentage(),
  writtenLineAmount: createEmptyAmount(),
  signedLineAmount: createEmptyAmount(),
  writtenLineCcyCode: "",
  signedLineCcyCode: "",
  statusCode: "",
  carrierReference: "",
  underwriterParty: createEmptyParty(),
  actuary: createEmptyParty(),
  contractSignatory: createEmptyParty(),
});

export const createEmptyClause = () => ({
  id: crypto.randomUUID(),
  code: "",
  description: "",
  text: "",
});

export const createEmptyScheduleEntry = () => ({
  id: crypto.randomUUID(),
  sequenceNbr: 0,
  description: "",
  references: [],
  classifications: [],
  amounts: [],
  dateTimes: [],
});

export const createEmptySchedule = () => ({
  id: crypto.randomUUID(),
  type: "",
  description: "",
  entries: [],
});

export const createEmptyRegulatoryProvision = () => ({
  id: crypto.randomUUID(),
  type: "",
  description: "",
  regulatoryAuthority: "",
  regulatoryRiskLocations: [],
  classifications: [],
  amounts: [],
  dateTimes: [],
  allocations: [],
});

export const createEmptyPremiumAdjustment = () => ({
  id: crypto.randomUUID(),
  description: "",
  creditPeriodDurationDays: 0,
  premiumAmountCurrencyCode: "",
  premiumAmount: createEmptyAmount(),
  rate: createEmptyPercentage(),
  minimumRate: createEmptyPercentage(),
  maximumRate: createEmptyPercentage(),
  basis: "",
  calculationType: "",
  calculationLoadingOperationCode: "",
  calculationLoadingFractionNumerator: 0,
  calculationLoadingFractionDenominator: 0,
  calculationFrequencyCode: "",
  calculationPeriodStartDateTime: createEmptyDateTime(),
  calculationPeriodEndDateTime: createEmptyDateTime(),
});
